import { FeatureSelector } from "mapbox-gl";
import { ref, Ref } from "vue";

export function usePlantingsLayerInteraction(
	map: Ref<mapboxgl.Map | null>,
	clickCallback: (plantingIds: number[]) => void,
) {
	const interactionEnabled = ref(false);
	const hoveredPlantingIds = ref<Set<number>>(new Set());

	function onMouseMove(e: mapboxgl.MapLayerMouseEvent) {
		if (map.value) map.value.getCanvas().style.cursor = "pointer";

		if (e.features?.length) {
			for (const id of hoveredPlantingIds.value) {
				map.value?.setFeatureState(
					{
						source: "plantings-boundaries",
						id,
					} as FeatureSelector,
					{ hover: false },
				);
			}

			hoveredPlantingIds.value.clear();

			for (const feature of e.features) {
				map.value?.setFeatureState(
					{
						source: "plantings-boundaries",
						id: feature.id,
					} as FeatureSelector,
					{ hover: true },
				);
				hoveredPlantingIds.value.add(feature.id as number);
			}
		}
	}

	function onMouseLeave() {
		if (map.value) map.value.getCanvas().style.cursor = "";

		for (const id of hoveredPlantingIds.value) {
			map.value?.setFeatureState(
				{
					source: "plantings-boundaries",
					id: id,
				} as FeatureSelector,
				{ hover: false },
			);
			hoveredPlantingIds.value.delete(id);
		}
	}

	function onMouseClick(e: mapboxgl.MapLayerMouseEvent) {
		if (!e.features?.length) return;

		clickCallback(e.features.map((f) => f.id as number));
	}

	function enablePlantingsInteraction() {
		if (!map.value) return;
		if (interactionEnabled.value) return;

		map.value?.on("mousemove", "plantings-fill", onMouseMove);
		map.value?.on("mouseleave", "plantings-fill", onMouseLeave);
		map.value?.on("click", "plantings-fill", onMouseClick);

		interactionEnabled.value = true;
	}

	function disablePlantingsInteraction() {
		if (!map.value) return;
		if (!interactionEnabled.value) return;

		map.value?.off("mousemove", "plantings-fill", onMouseMove);
		map.value?.off("mouseleave", "plantings-fill", onMouseLeave);
		map.value?.off("click", "plantings-fill", onMouseClick);

		interactionEnabled.value = false;
	}

	return {
		enablePlantingsInteraction,
		disablePlantingsInteraction,
	};
}
